import { gql, useMutation, useQuery } from '@apollo/client'
import { logEcomPurchase } from 'mParticle/ecommerceMParticleEvents'
import sendTrackingEvent from 'mParticle/sendTrackingEvent'
import { useRouter } from 'next/router'
import qs from 'qs'
import { Dispatch, useEffect } from 'react'

import Bugsnag, { NotifiableError } from '@bugsnag/js'
import {
  IAction,
  IErrorAction,
  IOrderSuccessAction,
  IPartialOrderSuccessAction,
} from 'checkout/containers/CheckoutStateReducer'
import { PartialOrder } from 'checkout/types/IOrder'
import getOrderInfo from 'checkout/utils/getOrderInfoFromQueryParams'
import { isTimeoutError, timeoutMessage } from 'checkout/utils/timeout'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { t } from 'localization'
import { useShoppingRegionContext } from 'shared/contexts/ShoppingRegionContextProvider'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { useAuth } from 'shared/hooks/useAuth'
import useCurrency from 'shared/hooks/useCurrency'
import { useUser } from 'shared/hooks/useUser'
import { formatMoneyObject } from 'shared/lib/formatMoneyObject'
import IPrice from 'shared/types/IPrice'

const TP = 'checkout.hooks.userOrder'

export const SHIPPING_ADDRESS_COUNT = 5

export const ORDER_PROPS = gql`
  fragment OrderProps on Order {
    id
    isAfterpayEligible
    isInstantShip
    status
    orderNumber
    billingInfoId
    shippingAddressId
    billingAddressId
    verificationNeeded
    priceBreakdownType
    shippingPrice {
      display
      value
      localizedValue
    }
    productPrice {
      display
      value
      localizedValue
    }
    taxPrice {
      display
      value
      localizedValue
    }
    vatPrice {
      display
      value
      localizedValue
    }
    finalPrice {
      display
      value
      localizedValue
    }
    listPrice {
      display
      value
      localizedValue
    }
    instantShipMarkupFeePrice {
      display
      value
      localizedValue
    }
    credits {
      display
      value
      localizedValue
    }
    shippingAddresses(count: $shippingAddressCount) {
      id
      name
      address1
      address2
      countryCode
      city
      state
      postalCode
    }
    shippingServiceLevelOptions {
      id
      name
      description
      disclaimer
      price {
        value
        localizedValue
        display
      }
      available
    }
    shippingServiceLevel {
      id
      name
      description
      disclaimer
      price {
        display
        value
        localizedValue
      }
      available
    }
    shippingAddress {
      id
      name
      address1
      address2
      city
      state
      stateCode
      postalCode
      country
      countryCode
      phone {
        display
        value
      }
    }
    billingAddress {
      id
      name
      address1
      address2
      city
      state
      stateCode
      postalCode
      country
      countryCode
      phone {
        display
        value
      }
    }
    billingInfo {
      ... on CreditCard {
        id
        last4Digits
        cardBrand
        paymentType
      }
      ... on AfterPay {
        id
        email
        paymentType
        name
      }
      ... on Affirm {
        id
        email
        name
        affirmCheckoutToken
        paymentType
      }
      ... on PayPal {
        id
        email
        paymentType
      }
      ... on Klarna {
        id
        email
        paymentType
      }
    }
    product {
      saleStatus
      size {
        display
        value
      }
      boxCondition
      shoeCondition
      mainPictureUrl
      productTemplate {
        id
        pictureUrl
        name
        color
        details
        sku
        slug
        brand
      }
    }
  }
`

export const PROMOTION_PROPS = gql`
  fragment PromotionProps on Order {
    promotion {
      id
      title
      description
      expiration
      value {
        display
        value
        localizedValue
      }
    }
  }
`

const PURCHASE_ORDER = gql`
  mutation buyOrder($input: BuyInput!) {
    buy(input: $input)
  }
`

export const getOrderQuery = (promotionsEnabled: boolean) => gql`
  query getOrder($orderNumber: Int, $shippingAddressCount: Int) {
    order(number: $orderNumber) {
      ...OrderProps
      ${promotionsEnabled ? '...PromotionProps' : ''}
    }
  }
  ${ORDER_PROPS}
  ${promotionsEnabled ? PROMOTION_PROPS : ''}
`

export const getCreateOrderMutation = (promotionsEnabled: boolean) => gql`
  mutation createOrder($orderInfo: CreateOrderInput!, $shippingAddressCount: Int) {
    createOrder(input: $orderInfo) {
      ...OrderProps
      ${promotionsEnabled ? '...PromotionProps' : ''}
    }
  }
  ${ORDER_PROPS}
  ${promotionsEnabled ? PROMOTION_PROPS : ''}
`

const GET_PRICE_BREAKDOWN = gql`
  query getPriceBreakdown(
    $id: ID!
    $countryCode: String
    $boxCondition: String
    $shoeCondition: String
    $size: String
    $priceCents: Int!
    $isInstantShip: Boolean
    $productId: String
  ) {
    getPriceBreakdown(
      id: $id
      countryCode: $countryCode
      boxCondition: $boxCondition
      shoeCondition: $shoeCondition
      size: $size
      priceCents: $priceCents
      isInstantShip: $isInstantShip
      productId: $productId
    ) {
      priceBreakdownType
      priorityFeePercent
      markupFee {
        display
        value
        localizedValue
      }
      basePrice {
        display
        value
        localizedValue
      }
      totalPrice {
        display
        value
        localizedValue
      }
    }
  }
`

interface IDataLogPurchase {
  order_number?: number
  product_template_slug?: string
  price?: number
  subtotal_price_dollars?: number
  currency_code?: string
  is_guest?: boolean
}

interface IPriceBreakdown {
  basePrice: IPrice
  markupFee: IPrice
  priceBreakdownType: string
}

interface IUseOrderProps {
  dispatch: Dispatch<IAction | IOrderSuccessAction | IPartialOrderSuccessAction | IErrorAction>
  displayCheckoutRTK?: boolean
  order?: PartialOrder
}

const useOrder = ({
  dispatch,
  displayCheckoutRTK = false,
  order: orderFromState,
}: IUseOrderProps) => {
  const router = useRouter()
  const { currentUser } = useUser()
  const { logoutUser } = useAuth()

  const promotionsEnabled = useFeatureFlag(FeatureFlag.PROMO_CODES_USER)
  const queryParams = qs.parse(router.asPath?.split('?')[1])
  const {
    boxCondition,
    condition,
    localizedExpectedPriceCents,
    expectedPriceCents,
    productTemplateId,
    productId,
    instantShip,
    details,
    mainPictureUrl,
    name,
    size,
    sizeDisplay,
  } = queryParams
  const [purchaseOrderMutation] = useMutation(PURCHASE_ORDER)
  const [createOrderMutation, { loading: creatingOrder }] = useMutation(
    getCreateOrderMutation(promotionsEnabled),
  )
  const { selectedCurrency, loading: fetchingSelectedCurrency } = useCurrency()
  const { refetch: fetchPriceBreakdownQuery, loading: fetchingPriceBreakdown } = useQuery(
    GET_PRICE_BREAKDOWN,
    { skip: true },
  )

  const { refetch: fetchOrderQuery, loading: fetchingOrder } = useQuery(
    getOrderQuery(promotionsEnabled),
    { skip: true },
  )

  const { country: selectedCountryCode } = useShoppingRegionContext()

  const { locale } = useRouter()

  const priceBreakdowId =
    productTemplateId ||
    productId ||
    orderFromState?.product?.productTemplate?.id ||
    orderFromState?.product?.id
  const priceBreakdownBoxCondition = boxCondition || orderFromState?.product?.boxCondition
  const priceBreakdownShoeCondition = condition || orderFromState?.product?.shoeCondition
  const priceBreakdownSize = `${size || orderFromState?.product?.size?.value}`
  const priceBreakdownPriceCents = Number(expectedPriceCents || orderFromState?.productPrice?.value)

  useEffect(() => {
    if (
      !orderFromState?.orderNumber &&
      priceBreakdowId &&
      priceBreakdownPriceCents &&
      !fetchingSelectedCurrency &&
      !displayCheckoutRTK
    ) {
      fetchPriceBreakdown()
    }
  }, [
    orderFromState?.orderNumber,
    priceBreakdowId,
    priceBreakdownPriceCents,
    fetchingSelectedCurrency,
    displayCheckoutRTK,
  ])

  const fetchOrder = async (orderNumberToFetch?: number) => {
    if (!orderNumberToFetch) {
      return
    }
    try {
      dispatch({ type: 'orderRequest' })
      const { data } = await fetchOrderQuery({
        orderNumber: orderNumberToFetch,
        shippingAddressCount: SHIPPING_ADDRESS_COUNT,
      })
      dispatch({ type: 'orderRequestSuccess', payload: data.order })
    } catch (error) {
      dispatch({ type: 'orderRequestFailure', payload: error?.message })
    }
  }

  const fetchPriceBreakdown = async () => {
    const shippingAddressCountry = orderFromState?.shippingAddress?.countryCode
    let priceBreakdown
    if (
      priceBreakdowId &&
      priceBreakdownBoxCondition &&
      priceBreakdownShoeCondition &&
      priceBreakdownSize &&
      priceBreakdownPriceCents
    ) {
      try {
        dispatch({ type: 'orderRequest' })
        const { data } = await fetchPriceBreakdownQuery({
          id: priceBreakdowId,
          countryCode: shippingAddressCountry || selectedCountryCode || 'US',
          boxCondition: priceBreakdownBoxCondition,
          shoeCondition: priceBreakdownShoeCondition,
          size: priceBreakdownSize,
          priceCents: priceBreakdownPriceCents,
          isInstantShip: orderFromState?.isInstantShip || (instantShip === 'true' ? true : false),
          productId: orderFromState?.product?.id || productId,
        })
        priceBreakdown = data.getPriceBreakdown
      } catch (error) {
        Bugsnag.notify(error as NotifiableError)
      }
    }
    if (productId || productTemplateId) {
      // If id is coming from query params 'productId' or 'productTemplateId', create partial order
      const partialOrder = await createPartialOrderFromQueryParams(priceBreakdown)
      dispatch({
        type: 'priceEstimateRequestSuccess',
        payload: partialOrder,
      })
    } else {
      // Order details already exist on order object, update listPrice & instantShipMarkupFeePrice
      const partialOrder = {
        listPrice: priceBreakdown?.basePrice,
        instantShipMarkupFeePrice: priceBreakdown?.markupFee,
        priceBreakdownType: priceBreakdown?.priceBreakdownType,
      }
      dispatch({
        type: 'priceEstimateRequestSuccess',
        payload: partialOrder,
      })
    }
  }

  const createPartialOrderFromQueryParams = async (priceBreakdown: IPriceBreakdown) => {
    const formatLocalizedCurrency = (amount: number) =>
      formatMoneyObject(
        { currency: selectedCurrency?.isoCode, amount },
        {
          useGrouping: true,
          showCents: true,
          disambiguatedCurrencySymbol: selectedCurrency?.disambiguatedSymbol,
        },
      ) || '$0.00'
    const priceZero = {
      value: 0,
      localizedValue: 0,
      display: formatLocalizedCurrency(0),
    }
    // TODO: hardcoded shipping value on frontend until we can get it from backend?
    const japanShippingPrice = {
      value: 100000,
      localizedValue: 100000,
      display: formatLocalizedCurrency(100000),
    }
    const useJapanShippingPrice =
      selectedCountryCode === 'JP' && selectedCurrency?.isoCode === 'JPY'
    // TODO: end
    const productPrice = {
      value: Number(expectedPriceCents),
      localizedValue: Number(localizedExpectedPriceCents),
      display: formatLocalizedCurrency(Number(localizedExpectedPriceCents)),
    }
    const partialOrder: PartialOrder = {
      isInstantShip: instantShip === 'true',
      // shipping displays $0.00 bc without an order we can't calc
      shippingPrice: useJapanShippingPrice ? japanShippingPrice : priceZero,
      productPrice,
      listPrice: priceBreakdown?.basePrice || productPrice,
      instantShipMarkupFeePrice: priceBreakdown?.markupFee || priceZero,
      // taxPrice displays $0.00 bc without an order we can't calc
      taxPrice: priceZero,
      finalPrice: {
        value: Number(expectedPriceCents),
        localizedValue: Number(localizedExpectedPriceCents),
        display: formatLocalizedCurrency(Number(localizedExpectedPriceCents)),
      },
      // credits displays $0.00 bc without an order we can't calc
      credits: priceZero,
      priceBreakdownType: priceBreakdown?.priceBreakdownType,
      product: {
        id: Number(productId),
        size: { display: String(sizeDisplay), value: Number(size) },
        mainPictureUrl: String(mainPictureUrl),
        productTemplate: {
          details: String(details),
          name: String(name),
          id: Number(productTemplateId),
        },
      },
    }
    return partialOrder
  }

  const createOrder = async () => {
    if (
      orderFromState?.orderNumber ||
      creatingOrder ||
      fetchingOrder ||
      fetchingPriceBreakdown ||
      fetchingSelectedCurrency
    ) {
      return
    }
    dispatch({ type: 'orderRequest' })
    try {
      const {
        data: { createOrder: createdOrder },
      } = await createOrderMutation({
        variables: {
          orderInfo: getOrderInfo(queryParams),
          shippingAddressCount: SHIPPING_ADDRESS_COUNT,
        },
      })
      dispatch({ type: 'orderRequestSuccess', payload: createdOrder })
      router.replace(
        `/checkout?orderNumber=${createdOrder.orderNumber}`,
        `/checkout/${createdOrder.orderNumber}`,
        { locale: router.locale },
      )
    } catch (error) {
      if (isTimeoutError(error)) {
        dispatch({ type: 'orderRequestFailure', payload: timeoutMessage() })
        await logoutUser()
        return
      }
      dispatch({
        type: 'orderRequestFailure',
        payload: t(
          `${TP}.orderRequestFailure1`,
          'Something went wrong.  Please try again or reach out to support@flightclub.com',
        ),
      })
    }
  }

  const purchaseOrder = async () => {
    try {
      if (!orderFromState?.orderNumber || Number.isNaN(orderFromState?.finalPrice?.value)) {
        throw new Error()
      }
      dispatch({ type: 'purchaseOrderRequest' })
      await purchaseOrderMutation({
        variables: {
          input: {
            orderNumber: orderFromState.orderNumber,
            finalPriceCents: orderFromState.finalPrice?.value,
          },
        },
      })

      const isGuest = !!currentUser?.isGuest

      const utm: {
        utm_source?: string | null
        utm_medium?: string | null
        utm_campaign?: string | null
      } = {}

      try {
        utm.utm_source = window.sessionStorage.getItem('utm_source')
        utm.utm_medium = window.sessionStorage.getItem('utm_medium')
        utm.utm_campaign = window.sessionStorage.getItem('utm_campaign')
      } catch (e) {
        Bugsnag.notify(e as NotifiableError)
      }

      sendTrackingEvent<IDataLogPurchase>('CART_PURCHASE', {
        currency_code: selectedCurrency?.isoCode,
        is_guest: isGuest,
        order_number: orderFromState?.orderNumber,
        price: orderFromState?.finalPrice?.value,
        product_template_slug: orderFromState?.product?.productTemplate?.slug,
        request_type: 'gql',
        subtotal_price_dollars:
          orderFromState?.finalPrice?.value && orderFromState?.finalPrice?.value / 100,
        ...utm,
      })

      logEcomPurchase(
        {
          priceCents: orderFromState?.productPrice?.value!,
          condition: orderFromState?.product?.shoeCondition,
          name: orderFromState?.product?.productTemplate?.name!,
          brandName: orderFromState?.product?.productTemplate?.brand,
          sku: orderFromState?.product?.productTemplate?.sku!,
        },
        {
          number: orderFromState?.orderNumber!,
          priceCents: orderFromState?.finalPrice?.value!,
        },
      )

      router.push(
        `/checkout/${orderFromState?.orderNumber}/success`,
        `/checkout/${orderFromState?.orderNumber}/success`,
        { locale },
      )
    } catch (err) {
      if (isTimeoutError(err)) {
        dispatch({ type: 'orderRequestFailure', payload: timeoutMessage() })
        await logoutUser()
        return
      }
      const message =
        err?.message && err.message !== 'undefined'
          ? err.message
          : t(
              `${TP}.orderRequestFailure2`,
              'Something went wrong.  Please try again or reach out to support@flightclub.com',
            )
      if (err?.error_code === 'delivery_dates_update') {
        // refresh order details if delivery date has changed after 2PM ET
        fetchOrder(orderFromState?.orderNumber)
      } else {
        Bugsnag.notify(err as NotifiableError)
      }
      dispatch({ type: 'purchaseOrderRequestFailure', payload: message })
    }
  }

  return { createOrder, purchaseOrder }
}

export default useOrder
